import React, { useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { ADD_MAP } from '../../../graphql/mutation'
import { GET_ALL_MAPS } from '../../../graphql/query'

interface IProps {
  closeMapModal: () => void
}

const MapModal: React.FC<IProps> = ({ closeMapModal }) => {
  const [addMap] = useMutation(ADD_MAP, {
    refetchQueries: [{ query: GET_ALL_MAPS }],
    context: { headers: { 'access-control-allow-methods': 'Configuration' } }
  })

  const { data, loading, error } = useQuery(GET_ALL_MAPS, {
    context: { headers: { 'access-control-allow-methods': 'Configuration' } }
  })

  const defaultConfigs =
    data?.getallMaps?.reduce((acc: Record<string, string>, item: any) => {
      acc[item.configName.replace(/"/g, '')] = item.value.replace(/"/g, '')
      return acc
    }, {}) || {}

  const initialValues = {
    services: 'Google Maps', // Static value for the service
    config1: defaultConfigs['API Key'] || '',
    config2: defaultConfigs['Client Id'] || '',
    config3: defaultConfigs['Email'] || '' // Removed Client Secret for MapMyIndia
  }

  const validationSchema = Yup.object({
    config1: Yup.string().required('API Key is required'),
    config2: Yup.string().required('Client Id is required'),
    config3: Yup.string().required('Email is required')
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const { config1, config2, config3 } = values

      const configName = ['API Key', 'Client Id', 'Email']
      const value = [config1, config2, config3]

      // Call the mutation with the mapped config names and values
      addMap({
        variables: {
          input: {
            services: 'Google Maps', // Static service name
            configName,
            value
          }
        }
      })
        .then((res: any) => {
          closeMapModal()
          res.data.addMaps.status === 200
            ? toast.success('Map service successfully added')
            : toast.error('Failed to add Map service')
        })
        .catch(() => {
          closeMapModal()
          toast.error('Error occurred while adding Map service')
        })
    }
  })

  // Reusable form field component
  const FormField = ({
    label,
    name,
    placeholder
  }: {
    label: string
    name: keyof typeof formik.touched
    placeholder: string
  }) => (
    <div className='row mb-6'>
      <label className='col-lg-4 col-form-label fw-semibold fs-6'>{label}</label>
      <div className='col-lg-8 fv-row'>
        <input
          type='text'
          {...formik.getFieldProps(name)}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            { 'is-invalid': formik.touched[name] && formik.errors[name] },
            { 'is-valid': formik.touched[name] && !formik.errors[name] }
          )}
          placeholder={placeholder}
        />
        {formik.touched[name] && formik.errors[name] && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors[name] as string}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  if (loading) return <h1>Loading...</h1>
  if (error) return <h1>Error while loading...</h1>

  // Configuration labels
  const configLabels = ['API Key', 'Client Id', 'Email']
  const configNames: any = ['config1', 'config2', 'config3']

  return (
    <div className='collapse show'>
      <form className='form' onSubmit={formik.handleSubmit}>
        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        <div className='card-body p-9'>
          <div className='row mb-6'>
            <h1 className='text-center'>Map Configuration</h1>
          </div>

          {/* Service Input */}
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-semibold fs-6 required'>Service</label>
            <div className='col-lg-8 fv-row'>
              <input
                type='text'
                className='form-control form-control-lg form-control-solid'
                value='Google Maps'
                disabled // Static value
              />
            </div>
          </div>

          {/* Config Name and Input Fields */}
          {configLabels.map((label, index) => (
            <FormField key={index} label={label} name={configNames[index]} placeholder={label} />
          ))}

          {/* Buttons */}
          <div className='d-flex justify-content-end mt-4'>
            <button type='button' className='btn btn-secondary me-4' onClick={closeMapModal}>
              Cancel
            </button>
            <button type='submit' className='btn btn-sm btn-primary' disabled={formik.isSubmitting}>
              Save
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default MapModal
